import React, { ReactNode, JSX } from 'react';

// Common styles for tour content
export const tourStyles = {
  container: { fontSize: '16px', lineHeight: '1.6' },
  h2: {
    fontSize: '24px',
    marginBottom: '16px',
    color: '#1976d2',
    display: 'flex',
    alignItems: 'center',
  },
  h3: {
    fontSize: '20px',
    marginBottom: '12px',
    color: '#1976d2',
    display: 'flex',
    alignItems: 'center',
  },
  paragraph: { fontSize: '16px', marginBottom: '12px' },
  list: {
    fontSize: '16px',
    marginBottom: '12px',
    paddingLeft: '0',
    listStyle: 'none',
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  nestedList: {
    fontSize: '16px',
    marginBottom: '12px',
    paddingLeft: '20px',
    listStyle: 'none',
  },
  nestedListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '8px',
  },
};

// Tour header template
export const TourHeader = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element => (
  <div style={tourStyles.container}>
    <h2 style={tourStyles.h2}>{title}</h2>
    {children}
  </div>
);

// Tour section template
export const TourSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): JSX.Element => (
  <div style={tourStyles.container}>
    <h3 style={tourStyles.h3}>{title}</h3>
    {children}
  </div>
);

// Tour paragraph template
export const TourParagraph = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => <p style={tourStyles.paragraph}>{children}</p>;

// Tour list template
export const TourList = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => <ul style={tourStyles.list}>{children}</ul>;

// Tour list item template
export const TourListItem = ({
  icon,
  children,
}: {
  icon?: ReactNode;
  children: ReactNode;
}): JSX.Element => (
  <li style={tourStyles.listItem}>
    <div
      style={{
        marginRight: '12px',
        display: 'flex',
        alignItems: 'center',
        width: '24px',
        height: '24px',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: '2px',
      }}
    >
      {children}
    </div>
  </li>
);

// Tour nested list template
export const TourNestedList = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => <ul style={tourStyles.nestedList}>{children}</ul>;

// Tour nested list item template
export const TourNestedListItem = ({
  icon,
  children,
}: {
  icon?: ReactNode;
  children: ReactNode;
}): JSX.Element => (
  <li style={tourStyles.nestedListItem}>
    {icon}
    <span>{children}</span>
  </li>
);

// Tour introduction content template
export const TourIntroduction = ({
  title,
  description,
  behaviorText,
  autoPlayText,
  additionalInfo,
}: {
  title: string;
  description: string;
  behaviorText?: string;
  autoPlayText: string;
  additionalInfo?: string;
}): JSX.Element => (
  <TourHeader title={title}>
    <TourParagraph>{description}</TourParagraph>
    {behaviorText && (
      <TourParagraph>
        <strong>Tour Behavior:</strong> {behaviorText}
      </TourParagraph>
    )}
    <TourParagraph>
      <strong>Auto-play:</strong> {autoPlayText}
    </TourParagraph>
    {additionalInfo && <TourParagraph>{additionalInfo}</TourParagraph>}
  </TourHeader>
);

// Tour welcome content template
export const TourWelcome = ({
  title,
  description,
}: {
  title: string;
  description: string;
}): JSX.Element => (
  <TourHeader title={title}>
    <TourParagraph>{description}</TourParagraph>
  </TourHeader>
);

// Tour completion content template
export const TourCompletion = ({
  title,
  congratulations,
  summary,
  reminder,
  nextStep,
}: {
  title: string;
  congratulations: string;
  summary: string;
  reminder: string;
  nextStep?: string;
}): JSX.Element => (
  <TourHeader title={title}>
    <TourParagraph>{congratulations}</TourParagraph>
    {summary && <TourParagraph>{summary}</TourParagraph>}
    {reminder && <TourParagraph>{reminder}</TourParagraph>}
    {nextStep && <TourParagraph>{nextStep}</TourParagraph>}
  </TourHeader>
);
